import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { CalendarDays, Clock, User, Mail, Phone, FileText } from "lucide-react";

const timeSlots = [
  "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", 
  "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"
];

export default function BookingForm({ bookingType, title }) {
  const [date, setDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        booking_type: bookingType,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: selectedTime
      });

      toast.success("Booking request submitted! We'll be in touch soon.");
      setFormData({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
      setDate(null);
      setSelectedTime("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#161616] border border-[#b8966a]/10 p-8 md:p-12" data-testid="booking-form">
      <h3 className="font-heading text-2xl text-[#f8f5f0] mb-2">{title}</h3>
      <p className="font-body text-[#a19d98] mb-8">
        Select your preferred date and time, and we'll confirm your appointment.
      </p>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Calendar */}
        <div>
          <label className="overline flex items-center gap-2 mb-4">
            <CalendarDays size={14} />
            Select Date
          </label>
          <div className="bg-[#0c0c0c] border border-[#b8966a]/10 p-4 inline-block">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(date) => date < new Date() || date.getDay() === 0}
              className="text-[#f8f5f0]"
              data-testid="booking-calendar"
            />
          </div>
        </div>

        {/* Time Slots */}
        <div>
          <label className="overline flex items-center gap-2 mb-4">
            <Clock size={14} />
            Select Time
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {timeSlots.map((time) => (
              <button
                key={time}
                type="button"
                onClick={() => setSelectedTime(time)}
                className={`py-3 px-4 font-sans text-xs tracking-wider border transition-all ${
                  selectedTime === time
                    ? "bg-[#b8966a] border-[#b8966a] text-[#0c0c0c]"
                    : "bg-transparent border-[#b8966a]/20 text-[#a19d98] hover:border-[#b8966a]/50"
                }`}
                data-testid={`time-slot-${time.replace(/\s/g, '-')}`}
              >
                {time}
              </button>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="overline flex items-center gap-2 mb-3">
              <User size={14} />
              Your Name
            </label>
            <input
              type="text"
              required
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              placeholder="Enter your name"
              data-testid="booking-name"
            />
          </div>
          <div>
            <label className="overline flex items-center gap-2 mb-3">
              <Mail size={14} />
              Email Address
            </label>
            <input
              type="email"
              required
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              placeholder="your@email.com"
              data-testid="booking-email"
            />
          </div>
        </div>

        <div>
          <label className="overline flex items-center gap-2 mb-3">
            <Phone size={14} />
            Phone Number
          </label>
          <input
            type="tel"
            required
            value={formData.customer_phone}
            onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
            placeholder="(612) 547-9260"
            data-testid="booking-phone"
          />
        </div>

        <div>
          <label className="overline flex items-center gap-2 mb-3">
            <FileText size={14} />
            Additional Notes
          </label>
          <textarea
            rows={4}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Tell us about your vision, preferences, or any special requests..."
            data-testid="booking-notes"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full md:w-auto disabled:opacity-50"
          data-testid="booking-submit"
        >
          {loading ? "Submitting..." : "Request Booking"}
        </button>
      </form>
    </div>
  );
}
